/**
 * The providers to draw a login button for, in display order.
 *
 * ONE BUTTON PER PROVIDER ID, because the id is all a click sends
 * (`redirectToProvider(provider.id)`): the server resolves which app answers
 * it, choosing the one app not marked `hidden`. Every Apple app — the web
 * Services ID, the iOS bundle id, the rows the mobile code exchange stores —
 * has the id "apple", so two Apple entries are always two copies of the same
 * button, whatever their client ids.
 *
 * The backend already leaves hidden apps out of this list
 * (core/adapters/allauth_socialaccount.py), so normally there is nothing to
 * drop; this is the invariant that makes a duplicate impossible whatever the
 * backend sends — during a rolling deploy, say, or after someone adds a
 * SocialApp by hand in the admin. It deliberately knows no product's bundle
 * id: the previous version skipped `ai.mrble.sharpana` by name, and showed two
 * Apple buttons the day a second product arrived with its own.
 *
 * allauth sends the list sorted by name (Apple, Google); it is reversed so
 * Google sits above Apple, as it always has.
 */
export function webLoginButtons<T extends { id: string }>(providers: readonly T[]): T[] {
  const seen = new Set<string>();
  const buttons: T[] = [];
  for (const provider of [...providers].reverse()) {
    if (seen.has(provider.id)) continue;
    seen.add(provider.id);
    buttons.push(provider);
  }
  return buttons;
}
